import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import PropTypes from "prop-types";
import { fieldStyles } from "services/utils";
import { addMeeting } from "services/api";
import { useTranslation } from "react-i18next";

const TimeDialog = ({ open, onClose, selectedDate, onConfirm, employeeId, applicantId }) => {
  // Hour + AM/PM combined
  const [hour, setHour] = useState("12");
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState("AM");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Hour options 1-12
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // Minute options 0-59
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

  const handleConfirm = async () => {
    // let h = hour;
    let h = parseInt(hour, 10);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;

    const dateTime = new Date(selectedDate);

    dateTime.setHours(h, minute, 0, 0);

    if (h === 0 && minute === 0) {
      dateTime.setSeconds(1);
      // Now time is 00:00:01.
      // This prevents "All Day" flags while looking like 12:00 AM to humans.
    }

    // --- FIX START ---
    // 1. Get the timezone offset in minutes (e.g., -120 for UTC+2)
    const offset = dateTime.getTimezoneOffset();

    // 2. Create a new date object adjusted by the offset
    // We subtract the offset because JS returns positive for behind UTC and negative for ahead
    const localDate = new Date(dateTime.getTime() - offset * 60 * 1000);

    // Convert to ISO string without timezone for Java LocalDateTime
    const formattedDateTime = localDate.toISOString().slice(0, 19);

    const meetingPayload = {
      employeeId: employeeId, // pass employeeId prop
      applicationId: applicantId, // pass applicantId prop
      dateTime: formattedDateTime, // send as JS Date
      comments: "", // empty string
    };

    try {
      setLoading(true);
      const result = await addMeeting(meetingPayload); // call API
      if (result !== "error") {
        //   onConfirm(result); // optional callback
        if (onConfirm) onConfirm(result);
      } else {
        console.error("Failed to add meeting");
      }
    } catch (err) {
      console.error("Error adding meeting:", err);
    }
    setLoading(false);
    // onConfirm(dateTime);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle> {t("admin.talentAcquisition.meetings.meetingTime")}</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={1} mt={1} mb={1}>
          {/* Hour */}
          <Select
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
            sx={{ ...fieldStyles, width: 80 }}
          >
            {hourOptions.map((h) => (
              <MenuItem key={h} value={h}>
                {h}
              </MenuItem>
            ))}
          </Select>

          {/* Minute */}
          <Select
            value={minute}
            onChange={(e) => setMinute(Number(e.target.value))}
            sx={{ ...fieldStyles, width: 80 }}
          >
            {minuteOptions.map((m) => (
              <MenuItem key={m} value={m}>
                {m.toString().padStart(2, "0")}
              </MenuItem>
            ))}
          </Select>

          {/* AM / PM */}
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            sx={{ ...fieldStyles, width: 80 }}
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </Select>
        </Box>
      </DialogContent>

      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      ) : (
        <DialogActions>
          {/* <Button onClick={onClose}>Cancel</Button> */}

          {/* <Button variant="contained" onClick={handleConfirm}>Confirm</Button> */}
          <Button
            onClick={handleConfirm}
            sx={{
              color: "blue",
              borderRadius: "8px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {t("common.confirm")}
          </Button>

          <Button
            onClick={onClose}
            sx={{
              color: "red",
              "&:hover": { backgroundColor: "red", color: "#fff" },
              borderRadius: "8px",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {t("common.cancel")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

TimeDialog.propTypes = {
  open: PropTypes.bool.isRequired, // Whether the dialog is open
  onClose: PropTypes.func.isRequired, // Callback to close the dialog
  selectedDate: PropTypes.instanceOf(Date), // The day selected from the calendar
  onConfirm: PropTypes.func.isRequired, // Callback returning the final Date object
  employeeId: PropTypes.string, // Employee ID
  applicantId: PropTypes.number, // Applicant ID
};

export default TimeDialog;
